import styled from "@emotion/styled";
import {
  type OpportunitiesSearchFilters,
  type OpportunitiesSearchOptions,
  type OpportunitySearch,
} from "@repo/api";
import { Table, TableBody, TableHeader, Variables } from "@repo/skapa";
import { t } from "@repo/translation";
import { trackAnalytics } from "@repo/utils";
import find from "lodash/find";
import {
  type ChangeEvent,
  type Dispatch,
  type SetStateAction,
  useEffect,
  useState,
} from "react";

import { ProjectActions } from "./ProjectActions";
import { ProjectsTableHeader } from "./ProjectsTableHeader";
import { ProjectsTableRow } from "./ProjectsTableRow";
import { TableActionButtons } from "./TableActionButtons";
import { addFiltersToSessionStorage } from "./filter/utils/sessionStorage";
import { type EditModalMode } from "./utils";

interface ProjectsTableProps {
  showQuickview: (opportunityId: number) => void;
  content?: OpportunitySearch[];
  options: OpportunitiesSearchOptions;
  setOptions: Dispatch<SetStateAction<OpportunitiesSearchOptions>>;
  filters: OpportunitiesSearchFilters;
}

const StyledTable = styled(Table)`
  && th:first-child,
  && td:first-child {
    padding-inline-start: ${Variables.space100};
  }
`;

export const ProjectsTable = ({
  content,
  showQuickview,
  options,
  setOptions,
  filters,
}: ProjectsTableProps) => {
  const [selectedOpportunities, setSelectedOpportunities] = useState<
    OpportunitySearch[]
  >([]);
  const [editModalMode, setEditModalMode] = useState<EditModalMode>("");

  useEffect(() => {
    addFiltersToSessionStorage({
      sortKey: options.sortKey,
      sortOrder: options.sortOrder,
    });
  }, [options.sortKey, options.sortOrder]);

  useEffect(() => {
    setSelectedOpportunities((prev) =>
      prev.filter((selected) =>
        content?.some((c) => c.opportunityId === selected.opportunityId)
      )
    );
  }, [content]);

  const handleSorting = (newSortKey: string) => {
    if (!newSortKey) {
      return;
    }
    if (newSortKey === options.sortKey) {
      setOptions({
        ...options,
        sortOrder: options.sortOrder === "asc" ? "desc" : "asc",
        pageIndex: 0,
      });
    } else {
      setOptions({ ...options, sortKey: newSortKey, pageIndex: 0 });
    }
  };

  const handleSelectAllChange = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectedOpportunities(checked ? (content ?? []) : []);
    trackAnalytics("select_all_opportunities");
  };

  const handleCheckboxChange = (
    e: ChangeEvent<HTMLInputElement>,
    project: OpportunitySearch
  ) => {
    const checked = e.target.checked;
    setSelectedOpportunities(
      checked
        ? [...selectedOpportunities, project]
        : selectedOpportunities.filter(
            (el) => el.opportunityId !== project.opportunityId
          )
    );
    trackAnalytics("selected_opportunities");
  };

  const hasContent = content && content.length > 0;
  const isAllSelected =
    hasContent && content?.length === selectedOpportunities.length;

  return (
    <>
      {selectedOpportunities.length > 0 && (
        <TableActionButtons setEditModalMode={setEditModalMode} />
      )}
      <StyledTable fullWidth aria-label={t("label.projects")}>
        <TableHeader emphasised>
          <ProjectsTableHeader
            isAllSelected={Boolean(isAllSelected)}
            onSelectAll={handleSelectAllChange}
            onSort={handleSorting}
            options={options}
          />
        </TableHeader>
        <TableBody striped>
          {content?.map((project) => (
            <ProjectsTableRow
              key={`row-${project.opportunityId}`}
              project={project}
              isSelected={Boolean(find(selectedOpportunities, project))}
              onShowQuickview={showQuickview}
              onCheckboxChange={handleCheckboxChange}
            />
          ))}
        </TableBody>
      </StyledTable>

      <ProjectActions
        editModalMode={editModalMode}
        setEditModalMode={setEditModalMode}
        selectedOpportunities={selectedOpportunities}
        setSelectedOpportunities={setSelectedOpportunities}
        filters={filters}
        options={options}
      />
    </>
  );
};
